import {
  DIAS_SEMANA,
  hojeUtc3,
  intervaloSemana,
  type Aluno,
  type AulaDetalheDto,
  type ChamadaHistoricoDto,
  type ChamadaDiaOpcaoDto,
  type ChamadaOpcoesDto,
  type EncontroDto,
  type LancarPresencaInput,
  type TurmaDto,
  type UpdateObservacoesAulaInput,
} from '@sheepcount/shared';
import { ChevronRight, Pencil } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { ChamadaHistorico } from '@/components/chamada-historico';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { PageLoading } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/toast';
import { api, ApiError } from '@/lib/api';
import { bloquearRecarga } from '@/lib/app-version';
import { useAuth } from '@/lib/auth';
import {
  apagarRascunho,
  lerRascunho,
  listarRascunhos,
  salvarRascunho,
  type ChamadaDraft,
} from '@/lib/chamada-draft';
import { formatDateBR, formatDateBRCurta } from '@/lib/format';
import { TurmaFilterSelect, turmaFiltroValido, useTurmaFilter } from '@/lib/turma-filter';
import { cn } from '@/lib/utils';

/** Dia escolhido para lançar a chamada (dia oferecido pela API ou data livre do admin). */
interface DiaEscolhido {
  data: string; // YYYY-MM-DD
  diaSemana: number;
}

type Step =
  | { kind: 'turma' }
  | { kind: 'dia'; turma: TurmaDto; opcoes: ChamadaOpcoesDto }
  | { kind: 'checklist'; turma: TurmaDto; dia: DiaEscolhido; opcoes: ChamadaOpcoesDto };

function diasLabel(turma: TurmaDto): string {
  return turma.diasSemana.map((d) => DIAS_SEMANA[d] ?? '').join(' e ');
}

/** Dia da semana (0=Dom … 6=Sáb) de uma data YYYY-MM-DD, sem depender do fuso do dispositivo. */
function diaSemanaDe(dataISO: string): number {
  return new Date(`${dataISO}T00:00:00Z`).getUTCDay();
}

/** Todos os dias selecionáveis (semana atual + próxima destravada + semanas anteriores pendentes). */
function diasSelecionaveis(opcoes: ChamadaOpcoesDto): ChamadaDiaOpcaoDto[] {
  return [
    ...opcoes.semanaAtual.dias.filter((d) => d.selecionavel),
    ...(opcoes.proximaSemana?.dias.filter((d) => d.selecionavel) ?? []),
    ...opcoes.semanasAnteriores.flatMap((s) => s.dias.filter((d) => d.selecionavel)),
  ];
}

/**
 * Dia pré-selecionado no passo 2, sobre os selecionáveis da semana atual:
 * hoje se for selecionável; senão o ocorrido mais recente. Se nada é
 * selecionável na semana atual e a próxima está destravada, o primeiro dia
 * selecionável da próxima.
 */
function preSelecionarDia(opcoes: ChamadaOpcoesDto, hoje: string): ChamadaDiaOpcaoDto | null {
  const atuais = opcoes.semanaAtual.dias.filter((d) => d.selecionavel);
  if (atuais.length === 0) {
    return opcoes.proximaSemana?.dias.find((d) => d.selecionavel) ?? null;
  }
  const deHoje = atuais.find((d) => d.data === hoje);
  if (deHoje) return deHoje;
  const ocorridos = atuais.filter((d) => d.ocorrido);
  if (ocorridos.length === 0) return null;
  return ocorridos.reduce((a, b) => (a.data > b.data ? a : b));
}

/** Checklist a partir da chamada gravada (vazio = todos FALTA). */
function checklistDe(aula: AulaDetalheDto | null): Record<string, boolean> {
  return aula ? Object.fromEntries(aula.alunos.map((a) => [a.alunoId, a.presente])) : {};
}

export function PresencaPage() {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const { turmaId: turmaFiltro } = useTurmaFilter();
  const [turmas, setTurmas] = useState<TurmaDto[] | null>(null);
  const [encontrosRecentes, setEncontrosRecentes] = useState<EncontroDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>({ kind: 'turma' });
  const [diaSelecionado, setDiaSelecionado] = useState<ChamadaDiaOpcaoDto | null>(null);
  const [dataLivre, setDataLivre] = useState('');
  const [alunosTurma, setAlunosTurma] = useState<Aluno[]>([]);
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [observacoes, setObservacoes] = useState('');
  const [loadingOpcoes, setLoadingOpcoes] = useState(false);
  const [loadingAlunos, setLoadingAlunos] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Chamada já gravada no dia aberto no checklist (null = primeiro lançamento). */
  const [chamadaExistente, setChamadaExistente] = useState<AulaDetalheDto | null>(null);
  const [rascunhoRestaurado, setRascunhoRestaurado] = useState(false);
  /** Nomes de quem estava presente e vai virar falta — pede confirmação antes de salvar. */
  const [confirmarRemocao, setConfirmarRemocao] = useState<string[] | null>(null);
  const [rascunhos, setRascunhos] = useState<ChamadaDraft[]>([]);

  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalhe, setDetalhe] = useState<AulaDetalheDto | null>(null);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [detalheError, setDetalheError] = useState<string | null>(null);
  const [editandoObs, setEditandoObs] = useState(false);
  const [obsDraft, setObsDraft] = useState('');
  const [salvandoObs, setSalvandoObs] = useState(false);
  const [editandoPresencas, setEditandoPresencas] = useState(false);
  const [presencasDraft, setPresencasDraft] = useState<Record<string, boolean>>({});
  const [salvandoPresencas, setSalvandoPresencas] = useState(false);
  const [restaurando, setRestaurando] = useState(false);

  // "Hoje" sempre em UTC-3 (Brasília) — nunca o fuso do dispositivo.
  const hoje = hojeUtc3();

  const load = useCallback(async () => {
    setError(null);
    try {
      // Turmas primeiro: o filtro salvo só vale se a turma ainda é visível.
      const turmasData = await api<TurmaDto[]>('/turmas');
      const filtro = turmaFiltroValido(turmaFiltro, turmasData);
      const recentes = await api<EncontroDto[]>(
        `/encontros/recentes?limit=6${filtro !== '' ? `&turmaId=${filtro}` : ''}`,
      );
      setTurmas(turmasData);
      setEncontrosRecentes(recentes);
      setRascunhos(listarRascunhos());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar turmas');
    }
  }, [turmaFiltro]);

  useEffect(() => {
    void load();
  }, [load]);

  // Deploy novo não recarrega a página no meio do wizard nem da edição pelo detalhe.
  const emEdicao = step.kind !== 'turma' || editandoPresencas || editandoObs;
  useEffect(() => (emEdicao ? bloquearRecarga() : undefined), [emEdicao]);

  async function openChecklist(turma: TurmaDto, dia: DiaEscolhido, opcoes: ChamadaOpcoesDto) {
    setLoadingAlunos(true);
    try {
      const [alunos, { aula }] = await Promise.all([
        api<Aluno[]>(`/turmas/${turma.id}/alunos`),
        api<{ aula: AulaDetalheDto | null }>(`/turmas/${turma.id}/chamada?data=${dia.data}`),
      ]);
      // Primeiro lançamento: todos começam como FALTA. Dia já lançado: parte do
      // que está gravado (nunca de "todos FALTA", que apagaria as presenças).
      // Rascunho local não salvo tem prioridade sobre ambos.
      const rascunho = lerRascunho(turma.id, dia.data);
      setAlunosTurma(alunos);
      setChamadaExistente(aula);
      setChecklist(rascunho?.checklist ?? checklistDe(aula));
      setObservacoes(rascunho?.observacoes ?? aula?.observacoes ?? '');
      setRascunhoRestaurado(rascunho !== null);
      setStep({ kind: 'checklist', turma, dia, opcoes });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao carregar alunos da turma');
    } finally {
      setLoadingAlunos(false);
    }
  }

  /** Atualiza o checklist/observações e grava o rascunho local a cada toque. */
  function editarChamada(next: { checklist?: Record<string, boolean>; observacoes?: string }) {
    if (step.kind !== 'checklist') return;
    const nextChecklist = next.checklist ?? checklist;
    const nextObservacoes = next.observacoes ?? observacoes;
    setChecklist(nextChecklist);
    setObservacoes(nextObservacoes);
    salvarRascunho({
      turmaId: step.turma.id,
      data: step.dia.data,
      checklist: nextChecklist,
      observacoes: nextObservacoes,
    });
  }

  function descartarRascunho() {
    if (step.kind !== 'checklist') return;
    apagarRascunho(step.turma.id, step.dia.data);
    setChecklist(checklistDe(chamadaExistente));
    setObservacoes(chamadaExistente?.observacoes ?? '');
    setRascunhoRestaurado(false);
    setRascunhos(listarRascunhos());
  }

  async function openDetalhe(aulaId: string) {
    setDetalheOpen(true);
    setDetalhe(null);
    setDetalheError(null);
    setEditandoObs(false);
    setEditandoPresencas(false);
    setDetalheLoading(true);
    try {
      setDetalhe(await api<AulaDetalheDto>(`/aulas/${aulaId}`));
    } catch (err) {
      setDetalheError(err instanceof Error ? err.message : 'Erro ao carregar a chamada');
    } finally {
      setDetalheLoading(false);
    }
  }

  async function handleSaveObservacoes() {
    if (!detalhe) return;
    const payload: UpdateObservacoesAulaInput = { observacoes: obsDraft.trim() };
    setSalvandoObs(true);
    try {
      const atualizado = await api<AulaDetalheDto>(`/aulas/${detalhe.aulaId}/observacoes`, {
        method: 'PATCH',
        body: payload,
      });
      setDetalhe(atualizado);
      setEditandoObs(false);
      toast('Observações salvas');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao salvar observações');
    } finally {
      setSalvandoObs(false);
    }
  }

  async function handleSalvarPresencas() {
    if (!detalhe || detalhe.alunos.length === 0) return;
    const payload: LancarPresencaInput = {
      turmaId: detalhe.turmaId,
      data: detalhe.data,
      // O upsert grava null quando `observacoes` está ausente; enviar sempre o
      // valor atual (ou string vazia) preserva a anotação existente da aula.
      observacoes: detalhe.observacoes ?? '',
      versaoEsperada: detalhe.versao,
      presencas: detalhe.alunos.map((aluno) => ({
        alunoId: aluno.alunoId,
        presente: presencasDraft[aluno.alunoId] ?? aluno.presente,
      })),
    };
    setSalvandoPresencas(true);
    try {
      await api('/presencas', { method: 'POST', body: payload });
      setDetalhe(await api<AulaDetalheDto>(`/aulas/${detalhe.aulaId}`));
      setEditandoPresencas(false);
      toast('Presenças atualizadas');
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Outra pessoa salvou no meio-tempo: mostra a versão atual para revisar.
        setDetalhe(await api<AulaDetalheDto>(`/aulas/${detalhe.aulaId}`));
        setEditandoPresencas(false);
      }
      toast(err instanceof Error ? err.message : 'Erro ao atualizar presenças');
    } finally {
      setSalvandoPresencas(false);
    }
  }

  /** Volta a chamada para um snapshot do histórico (só alunos que ainda estão na chamada). */
  async function handleRestaurar(registro: ChamadaHistoricoDto) {
    if (!detalhe) return;
    const doRegistro = new Map(registro.presencas.map((p) => [p.alunoId, p.presente]));
    const payload: LancarPresencaInput = {
      turmaId: detalhe.turmaId,
      data: detalhe.data,
      observacoes: registro.observacoes ?? '',
      versaoEsperada: detalhe.versao,
      presencas: detalhe.alunos.map((aluno) => ({
        alunoId: aluno.alunoId,
        presente: doRegistro.get(aluno.alunoId) ?? false,
      })),
    };
    setRestaurando(true);
    try {
      await api('/presencas', { method: 'POST', body: payload });
      toast('Versão restaurada');
      void load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao restaurar a versão');
    } finally {
      setDetalhe(await api<AulaDetalheDto>(`/aulas/${detalhe.aulaId}`).catch(() => detalhe));
      setRestaurando(false);
    }
  }

  async function selectTurma(turma: TurmaDto) {
    setLoadingOpcoes(true);
    try {
      const opcoes = await api<ChamadaOpcoesDto>(`/turmas/${turma.id}/chamada-opcoes`);
      const selecionaveis = diasSelecionaveis(opcoes);
      const unico = selecionaveis.length === 1 ? selecionaveis[0] : undefined;
      if (unico && !isAdmin) {
        // Uma única opção selecionável e sem poderes de admin: pula o passo 2.
        await openChecklist(turma, { data: unico.data, diaSemana: unico.diaSemana }, opcoes);
        return;
      }
      setDiaSelecionado(preSelecionarDia(opcoes, hoje));
      setDataLivre('');
      setStep({ kind: 'dia', turma, opcoes });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao carregar os dias da turma');
    } finally {
      setLoadingOpcoes(false);
    }
  }

  function back() {
    if (step.kind === 'checklist') {
      const selecionaveis = diasSelecionaveis(step.opcoes);
      if (!(selecionaveis.length === 1 && !isAdmin)) {
        const opcao = selecionaveis.find((d) => d.data === step.dia.data) ?? null;
        setDiaSelecionado(opcao);
        setDataLivre(opcao === null && isAdmin ? step.dia.data : '');
        setStep({ kind: 'dia', turma: step.turma, opcoes: step.opcoes });
        return;
      }
    }
    setStep({ kind: 'turma' });
  }

  async function handleSave(remocaoConfirmada = false) {
    if (step.kind !== 'checklist') return;

    // Relançamento que tira presença já gravada: confirma antes, listando quem.
    const removidos = (chamadaExistente?.alunos ?? []).filter(
      (aluno) => aluno.presente && !(checklist[aluno.alunoId] ?? false),
    );
    if (removidos.length > 0 && !remocaoConfirmada) {
      setConfirmarRemocao(removidos.map((aluno) => aluno.nome));
      return;
    }
    setConfirmarRemocao(null);

    const payload: LancarPresencaInput = {
      turmaId: step.turma.id,
      data: step.dia.data,
      // Sempre enviada: o campo já vem pré-carregado com a observação gravada.
      observacoes: observacoes.trim(),
      // Trava otimista: se alguém salvou depois que esta tela abriu, a API recusa.
      versaoEsperada: chamadaExistente?.versao ?? null,
      presencas: alunosTurma.map((aluno) => ({
        alunoId: aluno.id,
        presente: checklist[aluno.id] ?? false,
      })),
    };
    setSaving(true);
    try {
      await api('/presencas', { method: 'POST', body: payload });
      apagarRascunho(step.turma.id, step.dia.data);
      toast(chamadaExistente ? 'Chamada atualizada' : 'Presença salva com sucesso');
      setStep({ kind: 'turma' });
      setAlunosTurma([]);
      setChecklist({});
      setObservacoes('');
      setChamadaExistente(null);
      setRascunhoRestaurado(false);
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Outra pessoa salvou no meio-tempo. Mantém as marcações desta tela (e o
        // rascunho) e passa a comparar com a versão atual do servidor — o próximo
        // salvar confirma de novo se for remover presenças que o colega lançou.
        try {
          const { aula } = await api<{ aula: AulaDetalheDto | null }>(
            `/turmas/${step.turma.id}/chamada?data=${step.dia.data}`,
          );
          setChamadaExistente(aula);
        } catch {
          // segue com a versão antiga; o próximo salvar dará 409 de novo
        }
        toast(
          'Outra pessoa salvou esta chamada enquanto você editava. Revise as marcações e salve de novo.',
        );
        return;
      }
      const motivo = err instanceof Error ? err.message : 'Erro ao salvar presença';
      toast(`${motivo} — suas marcações continuam guardadas neste aparelho`);
    } finally {
      setSaving(false);
    }
  }

  const presentes = alunosTurma.filter((a) => checklist[a.id] ?? false).length;

  const algumSelecionavel = step.kind === 'dia' && diasSelecionaveis(step.opcoes).length > 0;
  const diaEscolhido: DiaEscolhido | null =
    step.kind !== 'dia'
      ? null
      : isAdmin && dataLivre !== ''
        ? { data: dataLivre, diaSemana: diaSemanaDe(dataLivre) }
        : diaSelecionado
          ? { data: diaSelecionado.data, diaSemana: diaSelecionado.diaSemana }
          : null;

  function renderSemana(
    titulo: string,
    semana: { inicio: string; fim: string; dias: ChamadaDiaOpcaoDto[] },
    nota?: string,
    notaClass = 'text-success',
  ) {
    return (
      <div className="mb-4">
        <div className="mb-2 text-[13px] font-semibold text-muted">
          {titulo} · {formatDateBRCurta(semana.inicio)}–{formatDateBRCurta(semana.fim)}
        </div>
        {nota && <div className={cn('mb-2 text-[11.5px]', notaClass)}>{nota}</div>}
        <div className="flex flex-wrap gap-2.5">
          {semana.dias.map((dia) => {
            const selecionado = dataLivre === '' && diaSelecionado?.data === dia.data;
            return (
              <button
                key={dia.data}
                type="button"
                disabled={!dia.selecionavel || loadingAlunos}
                onClick={() => {
                  setDiaSelecionado(dia);
                  setDataLivre('');
                }}
                className={cn(
                  'cursor-pointer rounded-[10px] border bg-surface px-[22px] py-3.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  selecionado
                    ? 'border-primary bg-primary-tint text-primary-dark'
                    : 'border-border hover:border-primary',
                )}
              >
                <span className="flex items-center gap-1.5">
                  {DIAS_SEMANA[dia.diaSemana] ?? ''} {formatDateBRCurta(dia.data)}
                  {dia.data === hoje && <Badge variant="primary">hoje</Badge>}
                </span>
                {!dia.ocorrido && !dia.selecionavel && (
                  <span className="mt-1 block text-[11px] font-normal text-muted">
                    ainda não ocorreu
                  </span>
                )}
                {dia.temChamada && (
                  <span className="mt-1 block text-[11px] font-normal text-success">
                    ✓ lançada
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const filtroAtivo = turmaFiltroValido(turmaFiltro, turmas ?? []);
  const turmasVisiveis =
    turmas === null || filtroAtivo === ''
      ? turmas
      : turmas.filter((turma) => turma.id === filtroAtivo);

  return (
    <div>
      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[22px] font-bold tracking-[-0.01em]">Marcar presença</h1>
        {step.kind === 'turma' && turmas !== null && <TurmaFilterSelect turmas={turmas} />}
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3">
          <div className="text-[13px] text-danger">{error}</div>
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Tentar novamente
          </Button>
        </div>
      ) : turmas === null ? (
        <PageLoading />
      ) : step.kind === 'turma' ? (
        <>
          <div className="mb-2.5 text-[13px] font-semibold text-muted">Escolha a turma</div>
          {(turmasVisiveis ?? []).length === 0 ? (
            <div className="mb-7 text-[13px] text-muted">Nenhuma turma cadastrada ainda.</div>
          ) : (
            <div className="mb-7 flex flex-col gap-2.5">
              {(turmasVisiveis ?? []).map((turma) => (
                <button
                  key={turma.id}
                  type="button"
                  disabled={loadingAlunos || loadingOpcoes}
                  onClick={() => void selectTurma(turma)}
                  className="flex cursor-pointer items-center justify-between gap-2.5 rounded-[10px] border border-border bg-surface p-4 text-left transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] disabled:opacity-60"
                >
                  <div className="min-w-0">
                    <div className="mb-1 break-words text-[15px] font-bold">{turma.nome}</div>
                    <div className="text-xs text-muted">
                      {diasLabel(turma)} · {turma.numAlunos} alunos
                    </div>
                    {rascunhos
                      .filter((r) => r.turmaId === turma.id)
                      .map((r) => (
                        <Badge key={r.data} variant="danger" className="mt-1.5 inline-block">
                          Chamada de {formatDateBRCurta(r.data)} não salva
                        </Badge>
                      ))}
                  </div>
                  <ChevronRight size={18} className="shrink-0 text-primary" />
                </button>
              ))}
            </div>
          )}

          <div className="mb-2.5 text-[13px] font-semibold text-muted">Encontros recentes</div>
          {encontrosRecentes.length === 0 ? (
            <div className="text-[13px] text-muted">Nenhum encontro registrado ainda.</div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {encontrosRecentes.map((encontro) => (
                <div
                  key={`${encontro.turmaId}-${encontro.semanaKey}`}
                  className="overflow-hidden rounded-[10px] border border-border bg-surface"
                >
                  <div className="flex items-center justify-between gap-2.5 border-b border-border px-3.5 py-3">
                    <div className="min-w-0">
                      <div className="break-words text-[13px] font-semibold">
                        Aula {encontro.numero} · {encontro.turmaNome}
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-muted">
                        Semana {formatDateBRCurta(encontro.semanaInicio)}–
                        {formatDateBRCurta(encontro.semanaFim)}
                      </div>
                    </div>
                    {!encontro.resolvido && <Badge variant="primary">Em andamento</Badge>}
                  </div>
                  {encontro.dias.map((dia) => (
                    <button
                      key={dia.aulaId}
                      type="button"
                      onClick={() => void openDetalhe(dia.aulaId)}
                      className="flex w-full cursor-pointer items-center justify-between gap-2.5 border-b border-[oklch(94%_0.004_90)] px-3.5 py-2.5 text-left transition-colors hover:bg-chip"
                    >
                      <div className="text-[12.5px] font-medium text-fg-soft">
                        {DIAS_SEMANA[dia.diaSemana] ?? ''} {formatDateBRCurta(dia.data)}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-primary-dark">
                          {dia.presentes}/{dia.total} presentes
                        </span>
                        <ChevronRight size={14} className="shrink-0 text-muted" />
                      </div>
                    </button>
                  ))}
                  {encontro.resolvido && (
                    <div className="px-3.5 py-2.5 text-[11.5px] text-muted">
                      Consolidado: {encontro.presentesConsolidado} de{' '}
                      {encontro.totalParticipantes} presentes na semana
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : step.kind === 'dia' ? (
        <>
          <button
            type="button"
            onClick={back}
            className="mb-4 cursor-pointer border-none bg-transparent p-0 text-[13px] font-semibold text-primary-dark"
          >
            ← Voltar
          </button>
          <div className="mb-3.5 text-base font-bold">{step.turma.nome} — escolha o dia</div>

          {renderSemana('Semana atual', step.opcoes.semanaAtual)}
          {step.opcoes.proximaSemana &&
            renderSemana(
              'Próxima semana',
              step.opcoes.proximaSemana,
              'Semana atual completa — próxima liberada',
            )}
          {step.opcoes.semanasAnteriores.map((semana) => (
            <div key={semana.inicio}>
              {renderSemana(
                'Semana anterior',
                semana,
                'Há dia(s) de aula sem chamada lançada',
                'text-muted',
              )}
            </div>
          ))}

          {isAdmin && (
            <div className="mt-1">
              <label
                htmlFor="data-livre-admin"
                className="mb-1.5 block text-[13px] font-semibold text-muted"
              >
                Outra data (admin)
              </label>
              <input
                id="data-livre-admin"
                type="date"
                value={dataLivre}
                onChange={(e) => {
                  setDataLivre(e.target.value);
                  if (e.target.value !== '') setDiaSelecionado(null);
                }}
                className="w-full max-w-[240px] rounded-lg border border-input bg-surface px-3 py-2.5 text-sm text-fg outline-none transition-colors focus:border-primary"
              />
              <div className="mt-1 text-[11.5px] text-muted">
                Como admin, você pode lançar a chamada para qualquer data.
              </div>
            </div>
          )}

          {!algumSelecionavel && !isAdmin ? (
            <div className="mt-4 rounded-lg bg-chip px-3.5 py-3 text-[13px] text-fg-soft">
              Nenhum dia de aula desta turma ocorreu nesta semana ainda
            </div>
          ) : (
            <Button
              className="mt-5"
              disabled={diaEscolhido === null || loadingAlunos}
              onClick={() => {
                if (diaEscolhido && step.kind === 'dia') {
                  void openChecklist(step.turma, diaEscolhido, step.opcoes);
                }
              }}
            >
              Continuar
            </Button>
          )}
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={back}
            className="mb-3.5 cursor-pointer border-none bg-transparent p-0 text-[13px] font-semibold text-primary-dark"
          >
            ← Voltar
          </button>
          <Card className="mb-4">
            <div className="mb-1 text-base font-bold">{step.turma.nome}</div>
            <div className="text-[12.5px] text-muted">
              Aula de {DIAS_SEMANA[step.dia.diaSemana] ?? ''} {formatDateBR(step.dia.data)} ·
              Semana {formatDateBRCurta(intervaloSemana(step.dia.data, step.turma.inicioCiclo).inicio)}–
              {formatDateBRCurta(intervaloSemana(step.dia.data, step.turma.inicioCiclo).fim)}
            </div>
          </Card>
          {rascunhoRestaurado && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-danger-bg px-3.5 py-3 text-[13px] text-danger">
              <span>Marcações não salvas restauradas deste aparelho. Confira e salve.</span>
              <Button variant="ghost" size="sm" onClick={descartarRascunho}>
                Descartar
              </Button>
            </div>
          )}
          {chamadaExistente && (
            <div className="mb-3 rounded-lg bg-primary-tint px-3.5 py-3 text-[13px] text-primary-dark">
              Esta chamada já foi lançada (
              {chamadaExistente.alunos.filter((a) => a.presente).length} presentes). Abaixo estão
              as presenças salvas — ajuste quem faltar e salve.
            </div>
          )}
          <div className="mb-2.5 text-[13px] text-muted">Toque nos alunos presentes</div>
          <div className="flex flex-col gap-2">
            {alunosTurma.map((aluno) => {
              const presente = checklist[aluno.id] ?? false;
              return (
                <button
                  key={aluno.id}
                  type="button"
                  onClick={() =>
                    editarChamada({
                      checklist: { ...checklist, [aluno.id]: !(checklist[aluno.id] ?? false) },
                    })
                  }
                  className={cn(
                    'flex min-h-[60px] w-full cursor-pointer items-center justify-between gap-3 rounded-[10px] border-none px-4 py-3.5 text-left transition-colors',
                    presente
                      ? 'bg-success-bg text-[oklch(30%_0.1_150)]'
                      : 'bg-[oklch(97%_0.006_90)] text-[oklch(45%_0.01_90)]',
                  )}
                >
                  <div className="min-w-0">
                    <div className="break-words text-[14.5px] font-semibold">{aluno.nome}</div>
                    {aluno.idade != null && (
                      <div className="text-[11.5px] opacity-75">{aluno.idade} anos</div>
                    )}
                  </div>
                  <span
                    className={cn(
                      'rounded-full px-3.5 py-1.5 text-xs font-bold',
                      presente
                        ? 'border border-transparent bg-success text-white'
                        : 'border border-[oklch(85%_0.01_90)] bg-surface text-muted',
                    )}
                  >
                    {presente ? 'Presente' : 'Falta'}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 pb-24">
            <label
              htmlFor="observacoes-aula"
              className="mb-1.5 block text-[13px] font-semibold text-muted"
            >
              Observações da aula
            </label>
            <textarea
              id="observacoes-aula"
              placeholder="Ex.: Parei o conteúdo em..."
              maxLength={2000}
              value={observacoes}
              onChange={(e) => editarChamada({ observacoes: e.target.value })}
              className="min-h-[80px] w-full resize-y rounded-lg border border-input bg-surface px-3 py-2.5 text-sm text-fg outline-none transition-colors focus:border-primary"
            />
          </div>
          <div className="fixed inset-x-0 bottom-[calc(60px_+_env(safe-area-inset-bottom))] z-30 flex items-center justify-between gap-3.5 border-t border-border bg-surface px-[18px] py-3.5 lg:bottom-0 lg:left-[232px]">
            <div className="text-[13px] font-semibold text-fg-soft">
              {presentes} de {alunosTurma.length} presentes
            </div>
            <Button
              size="lg"
              className="px-6 py-3"
              disabled={saving || alunosTurma.length === 0}
              onClick={() => void handleSave()}
            >
              {chamadaExistente ? 'Atualizar chamada' : 'Salvar presença'}
            </Button>
          </div>
        </>
      )}

      <Sheet
        open={confirmarRemocao !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmarRemocao(null);
        }}
      >
        <SheetContent>
          <SheetTitle className="mb-2">Remover presenças já salvas?</SheetTitle>
          <div className="mb-3 text-[13px] text-fg-soft">
            {confirmarRemocao?.length === 1
              ? 'Este aluno estava como PRESENTE e vai ficar com FALTA:'
              : `Estes ${confirmarRemocao?.length ?? 0} alunos estavam como PRESENTE e vão ficar com FALTA:`}
          </div>
          <ul className="mb-5 list-disc pl-5 text-[13px] font-semibold text-fg">
            {confirmarRemocao?.map((nome) => <li key={nome}>{nome}</li>)}
          </ul>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmarRemocao(null)}>
              Voltar e revisar
            </Button>
            <Button variant="destructive-outline" disabled={saving} onClick={() => void handleSave(true)}>
              Confirmar
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={detalheOpen} onOpenChange={setDetalheOpen}>
        <SheetContent>
          <SheetTitle className={detalhe ? 'mb-1' : undefined}>
            {detalhe ? `Aula ${detalhe.numero} — ${detalhe.turmaNome}` : 'Chamada'}
          </SheetTitle>
          {detalhe && (
            <div className="mb-[18px] text-[12.5px] text-muted">
              {DIAS_SEMANA[detalhe.diaSemana] ?? ''} {formatDateBR(detalhe.data)}
            </div>
          )}
          {detalheLoading ? (
            <PageLoading />
          ) : detalheError ? (
            <div className="text-[13px] text-danger">{detalheError}</div>
          ) : detalhe ? (
            <>
              <div className="mb-3 rounded-lg bg-chip px-3.5 py-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div className="text-[11.5px] font-semibold text-muted">Observações</div>
                  {!editandoObs && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setObsDraft(detalhe.observacoes ?? '');
                        setEditandoObs(true);
                      }}
                    >
                      <Pencil size={14} />
                      Editar
                    </Button>
                  )}
                </div>
                {editandoObs ? (
                  <>
                    <textarea
                      placeholder="Ex.: Parei o conteúdo em..."
                      maxLength={2000}
                      value={obsDraft}
                      onChange={(e) => setObsDraft(e.target.value)}
                      className="min-h-[80px] w-full resize-y rounded-lg border border-input bg-surface px-3 py-2.5 text-sm text-fg outline-none transition-colors focus:border-primary"
                    />
                    <div className="mt-2 flex items-center justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={salvandoObs}
                        onClick={() => setEditandoObs(false)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        disabled={salvandoObs}
                        onClick={() => void handleSaveObservacoes()}
                      >
                        Salvar
                      </Button>
                    </div>
                  </>
                ) : detalhe.observacoes !== null && detalhe.observacoes !== '' ? (
                  <div className="whitespace-pre-wrap break-words text-[13px] text-fg-soft">
                    {detalhe.observacoes}
                  </div>
                ) : (
                  <div className="text-[13px] italic text-muted">Sem observações</div>
                )}
              </div>
              {isAdmin && !editandoPresencas && detalhe.alunos.length > 0 && (
                <div className="mb-2 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPresencasDraft(
                        Object.fromEntries(detalhe.alunos.map((a) => [a.alunoId, a.presente])),
                      );
                      setEditandoPresencas(true);
                    }}
                  >
                    <Pencil size={14} />
                    Editar presenças
                  </Button>
                </div>
              )}
              <div className="flex flex-col gap-2">
                {editandoPresencas
                  ? detalhe.alunos.map((aluno) => {
                      const presente = presencasDraft[aluno.alunoId] ?? aluno.presente;
                      return (
                        <button
                          key={aluno.alunoId}
                          type="button"
                          onClick={() =>
                            setPresencasDraft((c) => ({
                              ...c,
                              [aluno.alunoId]: !(c[aluno.alunoId] ?? aluno.presente),
                            }))
                          }
                          className={cn(
                            'flex min-h-[60px] w-full cursor-pointer items-center justify-between gap-3 rounded-[10px] border-none px-4 py-3.5 text-left transition-colors',
                            presente
                              ? 'bg-success-bg text-[oklch(30%_0.1_150)]'
                              : 'bg-[oklch(97%_0.006_90)] text-[oklch(45%_0.01_90)]',
                          )}
                        >
                          <div className="min-w-0 break-words text-[14.5px] font-semibold">
                            {aluno.nome}
                          </div>
                          <span
                            className={cn(
                              'shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold',
                              presente
                                ? 'border border-transparent bg-success text-white'
                                : 'border border-[oklch(85%_0.01_90)] bg-surface text-muted',
                            )}
                          >
                            {presente ? 'Presente' : 'Falta'}
                          </span>
                        </button>
                      );
                    })
                  : detalhe.alunos.map((aluno) => (
                      <div
                        key={aluno.alunoId}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border px-3.5 py-3"
                      >
                        <div className="min-w-0 break-words text-sm font-semibold">
                          {aluno.nome}
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                          {aluno.presenteOutroDia && (
                            <Badge variant="primary">Presente em outro dia</Badge>
                          )}
                          <Badge variant={aluno.presente ? 'success' : 'default'}>
                            {aluno.presente ? 'Presente' : 'Falta'}
                          </Badge>
                        </div>
                      </div>
                    ))}
              </div>
              {editandoPresencas && (
                <div className="mt-3 flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={salvandoPresencas}
                    onClick={() => setEditandoPresencas(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    disabled={salvandoPresencas}
                    onClick={() => void handleSalvarPresencas()}
                  >
                    Salvar
                  </Button>
                </div>
              )}
              {!editandoPresencas && (
                <ChamadaHistorico
                  key={detalhe.aulaId}
                  aulaId={detalhe.aulaId}
                  versaoAtual={detalhe.versao}
                  restaurando={restaurando}
                  onRestaurar={handleRestaurar}
                />
              )}
            </>
          ) : null}
          <Button
            variant="secondary"
            className="mt-5 w-full"
            onClick={() => setDetalheOpen(false)}
          >
            Fechar
          </Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
